import React, { useState, useEffect } from 'react';
import {
  FiArrowDownLeft,
  FiArrowRight,
  FiArrowUpCircle,
  FiArrowUpRight,
  FiCreditCard,
  FiDollarSign,
  FiPlus,
  FiTrendingUp
} from 'react-icons/fi';
import API from '../api';
import usdtLogo from '../image/usdt-logo.svg';

const DashboardPage = ({ user, navigateTo }) => {
  // Dashboard-specific states
  const [recentTransactions, setRecentTransactions] = useState(null);
  const [totalDeposits, setTotalDeposits] = useState(null);
  const [totalCardTopups, setTotalCardTopups] = useState(null);

  const formatAmount = (value) => {
    if (value === null || value === undefined || Number.isNaN(parseFloat(value))) {
      return '0.00';
    }
    return parseFloat(value).toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  const startCreateCard = () => {
    sessionStorage.setItem('startCreateCard', 'true');
    navigateTo('cards', '/cards');
  };

  const displayName = user?.first_name || user?.username || 'Bienvenue';

  // Fetch recent transactions when component mounts or user changes
  useEffect(() => {
    if (user) {
      setRecentTransactions(null);
      API.get('/transactions/?limit=5')
        .then((res) => setRecentTransactions(res.data))
        .catch((err) => {
          console.error(err);
          setRecentTransactions([]);
        });
    }
  }, [user]);

  // Fetch dashboard stats when component mounts or user changes
  useEffect(() => {
    if (user) {
      API.get('/stats/')
        .then((res) => {
          setTotalDeposits(res.data.total_deposits);
          setTotalCardTopups(res.data.total_card_topups);
        })
        .catch((err) => {
          console.error('Failed to fetch dashboard stats', err);
        });
    }
  }, [user]);

  return (
    <section className="dashboard-section">
      <div className="dashboard-header">
        <div>
          <h2>Tableau de bord</h2>
          <p>Bonjour {displayName}, voici le résumé de votre compte.</p>
        </div>
      </div>

      <div className="dashboard-grid-compact">
        <div className="balance-cards-compact">
          <div className="balance-card-mini balance-card-primary">
            <div className="balance-card-icon usd">
              <FiDollarSign />
            </div>
            <div className="balance-card-info">
              <span className="balance-label">USD</span>
              <span className="balance-amount">
                {user ? formatAmount(user.usd_balance) : <span className="skeleton skeleton-text skeleton-width-50" />}
              </span>
              <span className="balance-helper">Solde principal</span>
            </div>
          </div>
          
          <div className="balance-card-mini">
            <div className="balance-card-icon mga">
              <span className="balance-card-currency">Ar</span>
            </div>
            <div className="balance-card-info">
              <span className="balance-label">MGA</span>
              <span className="balance-amount">
                {user ? formatAmount(user.balance_mga) : <span className="skeleton skeleton-text skeleton-width-50" />}
              </span>
              <span className="balance-helper">Ariary disponible</span>
            </div>
          </div>
          
          <div className="balance-card-mini">
            <div className="balance-card-icon usdt">
              <img src={usdtLogo} alt="USDT" className="balance-card-usdt-icon" />
            </div>
            <div className="balance-card-info">
              <span className="balance-label">USDT</span>
              <span className="balance-amount">
                {user ? formatAmount(user.balance_usdt) : <span className="skeleton skeleton-text skeleton-width-50" />}
              </span>
              <span className="balance-helper">Crypto wallet</span>
            </div>
          </div>
          
          <div className="balance-card-mini">
            <div className="balance-card-icon cards">
              <FiCreditCard />
            </div>
            <div className="balance-card-info">
              <span className="balance-label">Cartes</span>
              <span className="balance-amount">
                {user ? user.cards_count : <span className="skeleton skeleton-text skeleton-width-30" />}
              </span>
              <span className="balance-helper">Cartes actives</span>
            </div>
          </div>
        </div>

        <div className="dashboard-stats-actions">
          <div className="stats-section">
            <div className="stat-item">
              <span className="stat-icon-wrap positive"><FiTrendingUp className="stat-icon" /></span>
              <div className="stat-content">
                <span className="stat-label">Total des dépôts</span>
                <span className="stat-value">{totalDeposits !== null ? formatAmount(totalDeposits) : <span className="skeleton skeleton-text skeleton-width-50" />}</span>
              </div>
            </div>
            <div className="stat-item">
              <span className="stat-icon-wrap neutral"><FiArrowUpCircle className="stat-icon" /></span>
              <div className="stat-content">
                <span className="stat-label">Recharges cartes</span>
                <span className="stat-value">{totalCardTopups !== null ? formatAmount(totalCardTopups) : <span className="skeleton skeleton-text skeleton-width-50" />}</span>
              </div>
            </div>
          </div>
          
          <div className="actions-section">
            <button type="button" className="action-btn primary" title="Faire un dépôt" onClick={() => navigateTo('deposit', '/deposit')}>
              <FiTrendingUp />
              Ajouter des fonds
            </button>
            <button type="button" className="action-btn secondary desktop-only" title="Liste des cartes" onClick={() => navigateTo('cards', '/cards')}>
              <FiCreditCard />
              Mes cartes
            </button>
            <button type="button" className="action-btn accent" title="Créer une nouvelle carte" onClick={startCreateCard}>
              <FiPlus />
              Nouvelle carte
            </button>
          </div>
        </div>
      </div>

      <div className="recent-transactions-compact">
        <div className="section-header">
          <div>
            <span className="section-kicker">Activité</span>
            <h3>Transactions récentes</h3>
          </div>
          <button type="button" className="view-all-btn" onClick={() => navigateTo('transactions', '/transactions')}>
            Voir tout
            <FiArrowRight />
          </button>
        </div>
        {recentTransactions === null ? (
          <div className="transactions-skeleton">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="transaction-skeleton-item">
                <div className="skeleton skeleton-circle skeleton-circle-32" />
                <div className="skeleton-content">
                  <div className="skeleton skeleton-text skeleton-width-120 skeleton-height-14" />
                  <div className="skeleton skeleton-text skeleton-width-80 skeleton-height-12 skeleton-margin-top-4" />
                </div>
                <div className="skeleton skeleton-text skeleton-width-60 skeleton-height-14" />
              </div>
            ))}
          </div>
        ) : recentTransactions.length === 0 ? (
          <div className="no-transactions">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
            </svg>
            <p>Aucune transaction récente</p>
          </div>
        ) : (
          <div className="transactions-list-compact">
            {recentTransactions.slice(0, 5).map((tx) => {
              let typeLabel = tx.transaction_type;
              if (tx.transaction_type === 'card replenishment') {
                typeLabel = `Recharge *${tx.card_four_digits}`;
              } else if (tx.transaction_type === 'deposit') {
                const methodLabel = tx.payment_method === 'mvola' ? 'Mvola' : tx.payment_method === 'orange' ? 'OM' : '';
                typeLabel = `${tx.reference || 'Dépôt'} ${methodLabel}`;
              }
              const isDeposit = tx.transaction_type === 'deposit' || tx.payment_method === 'mvola' || tx.payment_method === 'orange';
              const amountSign = isDeposit ? '+' : '-';
              return (
                <div key={tx.id} className="transaction-item-compact">
                  <div className={`transaction-icon ${isDeposit ? 'deposit' : 'payment'}`}>
                    {isDeposit ? <FiArrowDownLeft /> : <FiArrowUpRight />}
                  </div>
                  <div className="transaction-details">
                    <span className="transaction-type">{typeLabel}</span>
                    <span className="transaction-date">{new Date(tx.date).toLocaleDateString()}</span>
                  </div>
                  <div className={`transaction-amount ${isDeposit ? 'deposit' : 'payment'}`}>
                    <span className="amount-value">{amountSign}{formatAmount(tx.amount)}</span>
                    <span className="amount-currency">{isDeposit ? 'MGA' : 'USD'}</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
};

export default DashboardPage;
